import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { type ReactNode, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { useCvIntake } from '../../api/cvIntake.ts';
import type { CvAnalyse, CvVerrijking, KandidaatProfiel } from '../../api/typen.ts';
import { FormulierDialoog } from '../../componenten/FormulierDialoog.tsx';
import { locatieLabel } from '../../componenten/keuzevelden.tsx';
import { useMeldingen } from '../../componenten/Meldingen.tsx';
import { datum } from '../../helpers/formatteer.ts';
import { label, opleidingsniveauLabels, rijbewijsLabels, taalniveauLabels, taalnaam } from '../../helpers/labels.ts';
import { adviesteksten, laadReferenties, type Referenties } from './KandidaatUitCvDialoog.tsx';

type Voorstel = CvAnalyse['voorstel'];
type Veld = 'voornaam' | 'achternaam' | 'geboortedatum' | 'email' | 'telefoon' | 'linkedinUrl' | 'straatHuisnummer';

const veldlabels: Record<Veld, string> = {
  voornaam: 'Voornaam',
  achternaam: 'Achternaam',
  geboortedatum: 'Geboortedatum',
  email: 'E-mailadres',
  telefoon: 'Telefoon',
  linkedinUrl: 'LinkedIn-profiel',
  straatHuisnummer: 'Straat en huisnummer',
};

interface Keuze {
  sleutel: string;
  titel: string;
  detail?: string | undefined;
  /** Standaard niet aangevinkt, omdat het al in het profiel staat of iets bestaands vervangt. */
  dubbel?: boolean | undefined;
  /** Waarom het niet is aangevinkt. */
  markering?: string | undefined;
}

const gelijk = (a: string | null | undefined, b: string | null | undefined) =>
  (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

const metMarkering = <T extends Keuze>(keuze: T): T => ({
  ...keuze,
  markering: keuze.dubbel ? 'staat al in profiel' : undefined,
});

/** Bepaalt wat er uit het cv nieuw of anders is ten opzichte van het profiel. */
function keuzes(voorstel: Voorstel, kandidaat: KandidaatProfiel, r: Referenties, analyse: CvAnalyse) {
  const k = voorstel.kandidaat;
  const velden: (Keuze & { veld: Veld; waarde: string })[] = [];
  for (const veld of Object.keys(veldlabels) as Veld[]) {
    const nieuw = k[veld];
    const huidig = kandidaat[veld];
    if (nieuw && !gelijk(nieuw, huidig)) {
      const toon = veld === 'geboortedatum' ? datum : (waarde: string) => waarde;
      velden.push({
        sleutel: `veld:${veld}`,
        veld,
        waarde: nieuw,
        titel: veldlabels[veld],
        detail: huidig ? `${toon(huidig)} → ${toon(nieuw)}` : toon(nieuw),
        dubbel: Boolean(huidig),
        markering: huidig ? 'vervangt huidige waarde' : undefined,
      });
    }
  }
  const nieuweLocatie = k.locatieId
    ? k.locatieId !== kandidaat.locatieId && r.locatie
      ? locatieLabel(r.locatie)
      : null
    : voorstel.locatie
      ? `${[voorstel.locatie.postcode, voorstel.locatie.plaats].filter(Boolean).join(' ')} (nieuw)`
      : null;
  if (nieuweLocatie) {
    velden.push({
      sleutel: 'veld:locatie',
      veld: 'straatHuisnummer',
      waarde: '',
      titel: 'Woonplaats',
      detail: kandidaat.locatie ? `${locatieLabel(kandidaat.locatie)} → ${nieuweLocatie}` : nieuweLocatie,
      dubbel: Boolean(kandidaat.locatie),
      markering: kandidaat.locatie ? 'vervangt huidige waarde' : undefined,
    });
  }
  const rijbewijzen = k.rijbewijzen.filter((r) => !kandidaat.rijbewijzen.includes(r));
  // Aanvullen, nooit vervangen: de bestaande rijbewijzen blijven staan.
  const alleRijbewijzen = [...kandidaat.rijbewijzen, ...rijbewijzen];

  const functietitels = k.functietitelIds
    .filter((id) => !kandidaat.functietitels.some((f) => f.functietitelId === id))
    .map((id) => ({ sleutel: `functietitel:${id}`, titel: r.functietitels.get(id)?.naam ?? 'Functietitel' }));
  const talentpools = analyse.talentpoolAdvies.kansen
    .filter((kans) => !kandidaat.talentpools.some((t) => t.talentpoolId === kans.talentpoolId))
    .filter((kans) => k.talentpoolIds.includes(kans.talentpoolId) || kans.kans >= 0.05)
    .slice(0, 5)
    .map((kans) => ({
      sleutel: `talentpool:${kans.talentpoolId}`,
      titel: `${kans.naam} (${Math.round(kans.kans * 100)}%)`,
      dubbel: !k.talentpoolIds.includes(kans.talentpoolId),
    }));

  const werkervaringen = voorstel.werkervaringen
    .map((w, i) => ({
      sleutel: `werkervaring:${i}`,
      titel: w.functietitelLetterlijk,
      detail: [w.werkgever, [datum(w.van), w.tot ? datum(w.tot) : 'heden'].filter(Boolean).join(' – ')]
        .filter(Boolean)
        .join(' · '),
      dubbel: kandidaat.werkervaringen.some(
        (b) => gelijk(b.functietitelLetterlijk, w.functietitelLetterlijk) && gelijk(b.werkgever, w.werkgever),
      ),
    }))
    .map(metMarkering);
  const opleidingen = voorstel.opleidingen
    .map((o, i) => ({
      sleutel: `opleiding:${i}`,
      titel: o.richting ?? label(opleidingsniveauLabels, o.niveau),
      detail: [o.niveau ? label(opleidingsniveauLabels, o.niveau) : null, o.instelling, o.eindjaar]
        .filter(Boolean)
        .join(' · '),
      dubbel: kandidaat.opleidingen.some((b) => gelijk(b.richting, o.richting) && b.niveau === o.niveau),
    }))
    .map(metMarkering);
  const certificaten = voorstel.certificaten
    .map((c, i) => ({
      sleutel: `certificaat:${i}`,
      titel: r.certificaattypes.get(c.certificaattypeId)?.naam ?? 'Certificaat',
      detail: c.behaaldOp ? `behaald ${datum(c.behaaldOp)}` : undefined,
      dubbel: kandidaat.certificaten.some((b) => b.certificaattypeId === c.certificaattypeId),
    }))
    .map(metMarkering);
  const vaardigheden = voorstel.vaardigheden
    .filter((v) => !kandidaat.vaardigheden.some((b) => b.vaardigheidId === v.vaardigheidId))
    .map((v) => ({
      sleutel: `vaardigheid:${v.vaardigheidId}`,
      titel: r.vaardigheden.get(v.vaardigheidId)?.naam ?? '',
    }));
  const talen = voorstel.talen
    .filter((t) => !kandidaat.talen.some((b) => b.taalcode === t.taalcode))
    .map((t) => ({
      sleutel: `taal:${t.taalcode}`,
      titel: taalnaam(t.taalcode),
      detail: label(taalniveauLabels, t.niveau),
    }));

  return {
    velden,
    rijbewijzen,
    alleRijbewijzen,
    functietitels,
    talentpools,
    werkervaringen,
    opleidingen,
    certificaten,
    vaardigheden,
    talen,
  };
}

type Keuzes = ReturnType<typeof keuzes>;

function standaardSelectie(lijsten: Keuzes): Set<string> {
  const alle: Keuze[] = [
    ...lijsten.velden,
    ...lijsten.functietitels,
    ...lijsten.talentpools,
    ...lijsten.werkervaringen,
    ...lijsten.opleidingen,
    ...lijsten.certificaten,
    ...lijsten.vaardigheden,
    ...lijsten.talen,
  ];
  const selectie = new Set(alle.filter((keuze) => !keuze.dubbel).map((keuze) => keuze.sleutel));
  if (lijsten.rijbewijzen.length > 0) {
    selectie.add('rijbewijzen');
  }
  return selectie;
}

function naarInvoer(voorstel: Voorstel, lijsten: Keuzes, selectie: Set<string>): CvVerrijking {
  const gekozen = (sleutel: string) => selectie.has(sleutel);
  const kandidaat: NonNullable<CvVerrijking['kandidaat']> = {};
  for (const veld of lijsten.velden) {
    if (veld.sleutel !== 'veld:locatie' && gekozen(veld.sleutel)) {
      kandidaat[veld.veld] = veld.waarde;
    }
  }
  const locatieGekozen = gekozen('veld:locatie');
  if (locatieGekozen && voorstel.kandidaat.locatieId) {
    kandidaat.locatieId = voorstel.kandidaat.locatieId;
  }
  if (gekozen('rijbewijzen')) {
    kandidaat.rijbewijzen = lijsten.alleRijbewijzen;
  }
  const index = (soort: string) => (_: unknown, i: number) => gekozen(`${soort}:${i}`);
  return {
    kandidaat,
    locatie: locatieGekozen && !voorstel.kandidaat.locatieId ? voorstel.locatie : null,
    functietitelIds: voorstel.kandidaat.functietitelIds.filter((id) => gekozen(`functietitel:${id}`)),
    talentpoolIds: lijsten.talentpools
      .filter((t) => gekozen(t.sleutel))
      .map((t) => t.sleutel.slice('talentpool:'.length)),
    werkervaringen: voorstel.werkervaringen.filter(index('werkervaring')),
    opleidingen: voorstel.opleidingen.filter(index('opleiding')),
    certificaten: voorstel.certificaten.filter(index('certificaat')),
    vaardigheden: voorstel.vaardigheden.filter((v) => gekozen(`vaardigheid:${v.vaardigheidId}`)),
    talen: voorstel.talen.filter((t) => gekozen(`taal:${t.taalcode}`)),
  };
}

interface Props {
  open: boolean;
  onSluit: () => void;
  kandidaat: KandidaatProfiel;
  cv: { id: string; bestandsnaam: string } | null;
}

/**
 * Verrijkt het profiel van een kandidaat met een van zijn cv's. AI leest het cv uit en Jev adviseert een
 * talentpool; de recruiter vinkt aan wat erbij komt. Wat al in het profiel staat is standaard niet aangevinkt, en
 * bestaande gegevens worden alleen vervangen als je dat expliciet kiest.
 * @param props De kandidaat, het cv en of de dialoog open is.
 * @returns De dialoog.
 */
export function ProfielVerrijkenDialoog({ open, onSluit, kandidaat, cv }: Props) {
  const intake = useCvIntake();
  const { meld } = useMeldingen();
  const [analyse, setAnalyse] = useState<CvAnalyse | null>(null);
  const [lijsten, setLijsten] = useState<Keuzes | null>(null);
  const [selectie, setSelectie] = useState<Set<string>>(new Set());
  const [fout, setFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);
  const analyseer = intake.analyseerCv.mutateAsync;

  useEffect(() => {
    if (!open || !cv) {
      return;
    }
    let actief = true;
    setAnalyse(null);
    setLijsten(null);
    setFout(null);
    (async () => {
      try {
        const resultaat = await analyseer(cv.id);
        const referenties = await laadReferenties(resultaat.voorstel.kandidaat.locatieId);
        if (!actief) {
          return;
        }
        const berekend = keuzes(resultaat.voorstel, kandidaat, referenties, resultaat);
        setAnalyse(resultaat);
        setLijsten(berekend);
        setSelectie(standaardSelectie(berekend));
      } catch (gevangen) {
        if (actief) {
          setFout(gevangen instanceof Error ? gevangen.message : 'Het cv kon niet worden uitgelezen.');
        }
      }
    })();
    return () => {
      actief = false;
    };
    // Bewust zonder `kandidaat`: alleen opnieuw analyseren bij een ander cv, niet als het profiel ververst.
  }, [open, cv?.id, analyseer]);

  const wissel = (sleutel: string) =>
    setSelectie((huidig) => {
      const nieuw = new Set(huidig);
      if (nieuw.has(sleutel)) {
        nieuw.delete(sleutel);
      } else {
        nieuw.add(sleutel);
      }
      return nieuw;
    });

  const verzend = async () => {
    if (!cv || !analyse || !lijsten) {
      return;
    }
    setBezig(true);
    setFout(null);
    try {
      await intake.verrijk.mutateAsync({
        cvId: cv.id,
        kandidaatId: kandidaat.id,
        invoer: naarInvoer(analyse.voorstel, lijsten, selectie),
      });
      meld('Profiel verrijkt uit cv');
      onSluit();
    } catch (gevangen) {
      setFout(gevangen instanceof Error ? gevangen.message : 'Opslaan is mislukt.');
    } finally {
      setBezig(false);
    }
  };

  const niets =
    lijsten !== null &&
    lijsten.velden.length +
      lijsten.rijbewijzen.length +
      lijsten.functietitels.length +
      lijsten.talentpools.length +
      lijsten.werkervaringen.length +
      lijsten.opleidingen.length +
      lijsten.certificaten.length +
      lijsten.vaardigheden.length +
      lijsten.talen.length ===
      0;

  return (
    <FormulierDialoog
      open={open}
      titel={`Profiel verrijken${cv ? ` met ${cv.bestandsnaam}` : ''}`}
      onSluit={onSluit}
      onVerzend={verzend}
      fout={lijsten ? fout : null}
      bezig={bezig || intake.analyseerCv.isPending}
      verzendLabel={niets ? 'Markeer als verwerkt' : `${selectie.size} onderdelen overnemen`}
      maxWidth="md"
    >
      <Stack spacing={2}>
        {!lijsten && !fout && (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              AI leest het cv uit en Jev kiest een talentpool…
            </Typography>
            <LinearProgress />
          </Box>
        )}
        {!lijsten && fout && <Alert severity="error">{fout}</Alert>}
        {analyse && lijsten && (
          <>
            <Typography variant="body2" color="text.secondary">
              Vink aan wat je wilt overnemen. Wat al in het profiel staat is niet aangevinkt; bestaande gegevens worden
              alleen vervangen als je dat aanvinkt.
            </Typography>
            {analyse.bestaandeKandidaatId && (
              <Alert severity="warning">
                Het e-mailadres in dit cv hoort bij een andere kandidaat.{' '}
                <Link component={RouterLink} to={`/kandidaten/${analyse.bestaandeKandidaatId}`}>
                  Bekijk die kandidaat
                </Link>
                .
              </Alert>
            )}
            {niets && <Alert severity="info">Dit cv bevat niets wat nog niet in het profiel staat.</Alert>}

            <Sectie titel="Persoonsgegevens" toon={lijsten.velden.length + lijsten.rijbewijzen.length > 0}>
              {lijsten.velden.map((veld) => (
                <Keuzeregel key={veld.sleutel} keuze={veld} aan={selectie.has(veld.sleutel)} onWissel={wissel} />
              ))}
              {lijsten.rijbewijzen.length > 0 && (
                <Keuzeregel
                  keuze={{
                    sleutel: 'rijbewijzen',
                    titel: 'Rijbewijzen toevoegen',
                    detail: lijsten.rijbewijzen.map((r) => label(rijbewijsLabels, r)).join(', '),
                  }}
                  aan={selectie.has('rijbewijzen')}
                  onWissel={wissel}
                />
              )}
            </Sectie>

            <Sectie
              titel="Indeling"
              icoon
              toon={
                lijsten.functietitels.length + lijsten.talentpools.length > 0 ||
                analyse.talentpoolAdvies.kansen.length > 0
              }
            >
              <Alert severity={analyse.talentpoolAdvies.status === 'toegewezen' ? 'success' : 'info'} sx={{ mb: 1 }}>
                {adviesteksten[analyse.talentpoolAdvies.status]}
              </Alert>
              {lijsten.talentpools.map((t) => (
                <Keuzeregel key={t.sleutel} keuze={t} aan={selectie.has(t.sleutel)} onWissel={wissel} />
              ))}
              {lijsten.functietitels.map((f) => (
                <Keuzeregel
                  key={f.sleutel}
                  keuze={{ ...f, detail: 'functietitel' }}
                  aan={selectie.has(f.sleutel)}
                  onWissel={wissel}
                />
              ))}
            </Sectie>

            <Lijstsectie titel="Werkervaring" keuzes={lijsten.werkervaringen} selectie={selectie} onWissel={wissel} />
            <Lijstsectie titel="Opleidingen" keuzes={lijsten.opleidingen} selectie={selectie} onWissel={wissel} />
            <Lijstsectie titel="Certificaten" keuzes={lijsten.certificaten} selectie={selectie} onWissel={wissel} />
            <Lijstsectie titel="Vaardigheden" keuzes={lijsten.vaardigheden} selectie={selectie} onWissel={wissel} />
            <Lijstsectie titel="Talen" keuzes={lijsten.talen} selectie={selectie} onWissel={wissel} />

            {(analyse.nietHerkend.vaardigheden.length > 0 || analyse.nietHerkend.certificaten.length > 0) && (
              <Alert severity="info">
                Niet in de referentielijsten gevonden en dus niet over te nemen:{' '}
                {[...analyse.nietHerkend.vaardigheden, ...analyse.nietHerkend.certificaten].join(', ')}.
              </Alert>
            )}
          </>
        )}
      </Stack>
    </FormulierDialoog>
  );
}

function Sectie({
  titel,
  toon,
  icoon,
  children,
}: {
  titel: string;
  toon: boolean;
  icoon?: boolean;
  children: ReactNode;
}) {
  if (!toon) {
    return null;
  }
  return (
    <Box>
      <Divider sx={{ mb: 1 }} />
      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        {icoon && <AutoAwesomeIcon fontSize="small" color="primary" />}
        {titel}
      </Typography>
      <Stack>{children}</Stack>
    </Box>
  );
}

function Lijstsectie({
  titel,
  keuzes: lijst,
  selectie,
  onWissel,
}: {
  titel: string;
  keuzes: Keuze[];
  selectie: Set<string>;
  onWissel: (sleutel: string) => void;
}) {
  return (
    <Sectie titel={titel} toon={lijst.length > 0}>
      {lijst.map((keuze) => (
        <Keuzeregel key={keuze.sleutel} keuze={keuze} aan={selectie.has(keuze.sleutel)} onWissel={onWissel} />
      ))}
    </Sectie>
  );
}

function Keuzeregel({ keuze, aan, onWissel }: { keuze: Keuze; aan: boolean; onWissel: (sleutel: string) => void }) {
  return (
    <FormControlLabel
      control={<Checkbox size="small" checked={aan} onChange={() => onWissel(keuze.sleutel)} />}
      label={
        <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
          <Typography variant="body2">{keuze.titel}</Typography>
          {keuze.detail && (
            <Typography variant="caption" color="text.secondary">
              {keuze.detail}
            </Typography>
          )}
          {keuze.markering && <Chip size="small" variant="outlined" label={keuze.markering} />}
        </Stack>
      }
    />
  );
}
